import * as React from 'react';
import { useLocalStorage } from './useLocalStorage';

interface IMeetingLogEntry {
	status: 'logged' | 'noShow';
	timestamp: number;
}

type MeetingLogStorage = Record<string, IMeetingLogEntry>;

const STORAGE_KEY = 'meeting-notes-log-status';
const TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

/**
 * Hook that persists meeting log status to localStorage with 7-day TTL expiration.
 * This allows users to see which meetings they've logged or marked as no-show
 * even after refreshing the page or closing the browser.
 */
export function useMeetingLogStorage() {
	const [meetingLogs, setMeetingLogs] = useLocalStorage<MeetingLogStorage>({
		key: STORAGE_KEY,
		initialValue: {},
	});

	// Clean up expired entries on mount only.
	// Why empty deps are correct here:
	// 1. STORAGE_KEY is a constant - the key never changes
	// 2. Adding meetingLogs to deps would cause an infinite loop (cleanup updates state → triggers effect)
	// 3. Expired entries are also lazily filtered in isMeetingLogged/isMeetingNoShow, so users never see stale data
	// 4. Running cleanup only on mount is sufficient since TTL is 7 days - no need to re-run during a session
	React.useEffect(() => {
		const now = Date.now();
		const entries = Object.entries(meetingLogs || {});
		const validEntries = entries.filter(([, entry]) => now - entry.timestamp < TTL_MS);

		// Only update if we removed some entries
		if (validEntries.length < entries.length) {
			setMeetingLogs(Object.fromEntries(validEntries));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally run only on mount (see comment above)
	}, []);

	const isMeetingLogged = (meetingId: string): boolean => {
		const entry = meetingLogs?.[meetingId];
		if (!entry) return false;

		// Check if entry is expired
		if (Date.now() - entry.timestamp >= TTL_MS) return false;

		return entry.status === 'logged';
	};

	const isMeetingNoShow = (meetingId: string): boolean => {
		const entry = meetingLogs?.[meetingId];
		if (!entry) return false;

		// Check if entry is expired
		if (Date.now() - entry.timestamp >= TTL_MS) return false;

		return entry.status === 'noShow';
	};

	const markMeetingLogged = (meetingId: string): void => {
		setMeetingLogs(prevLogs => ({
			...prevLogs,
			[meetingId]: {
				status: 'logged',
				timestamp: Date.now(),
			},
		}));
	};

	const markMeetingNoShow = (meetingId: string): void => {
		setMeetingLogs(prevLogs => ({
			...prevLogs,
			[meetingId]: {
				status: 'noShow',
				timestamp: Date.now(),
			},
		}));
	};

	return {
		isMeetingLogged,
		isMeetingNoShow,
		markMeetingLogged,
		markMeetingNoShow,
	};
}
